import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, X, ArrowRight, Zap, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * UpgradeNudgeModal — contextual upgrade prompt for free users.
 * Use it when a free user hits a gated action (resume limit, locked template,
 * cover letter, etc).
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - trigger: string (e.g. "resume_limit", "locked_template", "cover_letter")
 *  - headline?: string (override)
 *  - description?: string (override)
 */

const DEFAULT_COPY = {
  resume_limit: {
    headline: "You've used your free resume this month",
    description: "Upgrade to Pro for unlimited resume optimizations, AI rewrites, cover letters, and all 12 Studio themes — $39/mo.",
  },
  locked_template: {
    headline: "This template is a Pro perk",
    description: "Pro unlocks all 12 Resume Studio themes (industry-specific, executive, creative) plus unlimited resume optimizations.",
  },
  cover_letter: {
    headline: "AI Cover Letters are a Pro feature",
    description: "Generate a tailored cover letter from your resume and any job description in under 30 seconds.",
  },
  linkedin_optimizer: {
    headline: "LinkedIn Optimizer is a Pro feature",
    description: "Pro rewrites your LinkedIn headline, about section, and work experience to match your target role.",
  },
  interview_prep: {
    headline: "AI Interview Prep is a Pro feature",
    description: "Get role-specific interview questions + AI-graded answer feedback based on your resume and target job.",
  },
  default: {
    headline: "Unlock Ascend Pro",
    description: "Unlimited resume optimizations, AI cover letters, LinkedIn rewrites, interview prep, and Sage AI coaching.",
  },
};

const PRO_FEATURES = [
  'Unlimited resume optimizations',
  'Unlimited AI rewrites + cover letters',
  'Unlimited Sage AI coaching',
  'Interview prep + LinkedIn optimizer',
  'All 12 Resume Studio themes',
  'Clean PDF + .docx exports',
];

export default function UpgradeNudgeModal({ open, onClose, trigger = 'default', headline, description }) {
  const copy = DEFAULT_COPY[trigger] || DEFAULT_COPY.default;

  const trackClick = (cta) => {
    if (window.gtag) {
      window.gtag('event', 'upgrade_nudge_click', { trigger, cta });
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={onClose}
          data-testid="upgrade-nudge-overlay"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.2 }}
            className="relative bg-white max-w-lg w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="upgrade-nudge-modal"
          >
            <button
              onClick={onClose}
              className="absolute top-3 right-3 p-1 text-[#6b7280] hover:text-[#0f1115] transition-colors"
              aria-label="Close"
              data-testid="upgrade-nudge-close"
            >
              <X size={18} />
            </button>

            <div className="bg-gradient-to-br from-[#0f1115] via-[#001d6a] to-[#002fa7] text-white p-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 text-xs font-semibold uppercase tracking-wider mb-3">
                <Sparkles size={12} /> Ascend Pro
              </div>
              <h2 className="font-satoshi text-2xl font-black leading-tight mb-2">
                {headline || copy.headline}
              </h2>
              <p className="text-white/80 text-sm">
                {description || copy.description}
              </p>
            </div>

            <div className="p-6">
              <ul className="grid grid-cols-1 gap-2 mb-6">
                {PRO_FEATURES.map(f => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#4b5563]">
                    <Check size={16} className="text-[#10b981] mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col gap-2">
                <Link
                  to={`/pricing?ref=nudge&trigger=${trigger}`}
                  onClick={() => trackClick('view_pricing')}
                  className="w-full px-5 py-3 bg-[#002fa7] text-white font-bold hover:bg-[#001d6a] transition-colors inline-flex items-center justify-center gap-2"
                  data-testid="upgrade-nudge-cta"
                >
                  <Zap size={16} /> Upgrade to Pro
                  <ArrowRight size={16} />
                </Link>
                <button
                  onClick={() => { trackClick('dismiss'); onClose(); }}
                  className="w-full py-2 text-sm text-[#6b7280] hover:text-[#0f1115] transition-colors"
                  data-testid="upgrade-nudge-later"
                >
                  Not now
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
